import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Ban, Check, ChevronLeft, ChevronRight, ExternalLink, FileText, Music, X } from "lucide-react";
import type { LibraryItem } from "@/api/models/api-types";
import { DetailAmbient } from "@/features/library/components/DetailAmbient";
import { getFileExtension, getItemUrl, getMediaKind } from "@/features/library/lib/library-media";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { useLibraryStore } from "@/features/library/store/library-store";
import { cn } from "@/lib/utils";
import { usePickerLibrary } from "../hooks/use-picker-library";
import { getPickCompatibility, toSelectedFile } from "../lib/pick-compatibility";
import { pickerPrimaryButtonClass } from "../lib/picker-ui";
import { useFilePickerStore } from "../store/file-picker-store";
import { PickerFrameStrip } from "./PickerFrameStrip";

const DEFAULT_MAX_SELECTION = 10;

const roundButtonClass = cn(
  libraryGlowButtonClass,
  "w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-black/[0.05] dark:bg-white/[0.08] text-text-secondary dark:text-gray-200 hover:text-text-primary dark:hover:text-white ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08] backdrop-blur-md disabled:opacity-30 disabled:pointer-events-none"
);

const QuickLookMedia = ({ item, videoRef }: { item: LibraryItem; videoRef: React.RefObject<HTMLVideoElement | null> }) => {
  const { t } = useTranslation();
  const setActiveVideoEl = useLibraryStore((state) => state.setActiveVideoEl);
  const kind = getMediaKind(item);
  const url = getItemUrl(item);

  // Ambiyans, detay ekranındaki gibi aktif video elemanından bir kare alır
  useEffect(() => {
    const video = videoRef.current;
    if (kind !== "video" || !video) return;
    setActiveVideoEl(video);
    return () => {
      if (useLibraryStore.getState().activeVideoEl === video) setActiveVideoEl(null);
    };
  }, [kind, setActiveVideoEl, videoRef]);

  if (kind === "image") {
    return (
      <img
        src={url}
        alt={item.title}
        className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl"
      />
    );
  }
  if (kind === "video") {
    return (
      <video
        ref={videoRef}
        src={url}
        controls
        autoPlay
        muted
        loop
        playsInline
        className="max-h-full max-w-full rounded-2xl bg-black shadow-2xl"
      />
    );
  }
  if (kind === "audio") {
    return (
      <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-3xl bg-gradient-to-br from-violet-100 to-sky-100 dark:from-violet-950 dark:to-sky-950 p-8 shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-white/70 dark:bg-white/10 flex items-center justify-center">
          <Music className="w-7 h-7 text-violet-600 dark:text-violet-300" />
        </div>
        <p className="text-center text-sm text-text-secondary dark:text-gray-300 line-clamp-3">
          {item.prompt || item.title || t("library.audio")}
        </p>
        <audio src={url} controls className="w-full" />
      </div>
    );
  }
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-3xl bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-950 dark:to-orange-950 p-8 shadow-2xl">
      <div className="w-16 h-16 rounded-full bg-white/70 dark:bg-white/10 flex items-center justify-center">
        <FileText className="w-7 h-7 text-rose-600 dark:text-rose-300" />
      </div>
      <span className="text-sm font-semibold text-text-secondary dark:text-gray-300">
        {getFileExtension(item).toUpperCase() || "DOC"}
      </span>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 dark:text-violet-300 hover:underline"
      >
        <ExternalLink className="w-4 h-4" />
        {t("filePicker.quickLook.openFile")}
      </a>
    </div>
  );
};

/**
 * Seçici içinde açılan hızlı önizleme. Esc yalnızca bu katmanı kapatır (modal
 * Esc'i yakalar); oklar listede gezdirir.
 */
export const PickerQuickLook = () => {
  const { t } = useTranslation();
  const quickLookId = useFilePickerStore((state) => state.quickLookId);
  const setQuickLookId = useFilePickerStore((state) => state.setQuickLookId);
  const config = useFilePickerStore((state) => state.config);
  const selectFile = useFilePickerStore((state) => state.selectFile);
  const removeFile = useFilePickerStore((state) => state.removeFile);
  const isSelected = useFilePickerStore((state) =>
    state.selectedFiles.some((file) => file.id === quickLookId)
  );
  const isFull = useFilePickerStore(
    (state) => state.selectedFiles.length >= (state.config?.maxSelection || DEFAULT_MAX_SELECTION)
  );
  const { items, hasNextPage, fetchNextPage } = usePickerLibrary();
  const videoRef = useRef<HTMLVideoElement>(null);

  const index = items.findIndex((item) => item._id === quickLookId);
  const item = index >= 0 ? items[index] : null;
  const compatibility = useMemo(() => (item ? getPickCompatibility(item, config) : null), [item, config]);

  const goTo = useCallback(
    (offset: number) => {
      const next = items[index + offset];
      if (next) setQuickLookId(next._id);
      // Listenin sonuna yaklaşınca sonraki sayfa önden yüklenir
      if (offset > 0 && index + offset >= items.length - 3 && hasNextPage) void fetchNextPage();
    },
    [fetchNextPage, hasNextPage, index, items, setQuickLookId]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "VIDEO" || target?.tagName === "AUDIO") return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goTo(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goTo(1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goTo]);

  // Öğe listeden düştüyse (filtre değişti) katman kapanır
  useEffect(() => {
    if (quickLookId && !item) setQuickLookId(null);
  }, [item, quickLookId, setQuickLookId]);

  if (!item || !compatibility) return null;

  const isMulti = (config?.maxSelection || DEFAULT_MAX_SELECTION) > 1;
  const isBlocked = compatibility.status === "blocked";
  const isFrameOnly = compatibility.status === "frame";
  const isLimitReached = isMulti && isFull && !isSelected;

  const handleToggle = () => {
    if (isSelected) {
      removeFile(item._id);
      return;
    }
    if (isBlocked || isLimitReached) return;
    selectFile(toSelectedFile(item), item);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      role="dialog"
      aria-label={item.title || t("filePicker.quickLook.open")}
      className="absolute inset-0 z-40 flex flex-col overflow-hidden bg-bg-primary dark:bg-gray-950"
    >
      <DetailAmbient key={item._id} item={item} />

      <div className="relative flex items-center gap-3 px-3 sm:px-5 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-4 pb-3">
        <button
          type="button"
          onClick={() => setQuickLookId(null)}
          aria-label={t("common.close")}
          className={roundButtonClass}
        >
          <X className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-text-primary dark:text-white">
            {item.title || t("filePicker.untitled")}
          </p>
          {item.prompt && (
            <p className="truncate text-xs text-text-tertiary dark:text-gray-400">{item.prompt}</p>
          )}
        </div>
        <span className="shrink-0 text-xs tabular-nums text-text-tertiary dark:text-gray-400">
          {index + 1} / {items.length}
        </span>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center gap-2 sm:gap-4 px-2 sm:px-5">
        <button
          type="button"
          onClick={() => goTo(-1)}
          disabled={index <= 0}
          aria-label={t("common.previous")}
          className={cn(roundButtonClass, "hidden sm:flex")}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div key={item._id} className="flex h-full min-w-0 flex-1 items-center justify-center py-2">
          <QuickLookMedia item={item} videoRef={videoRef} />
        </div>
        <button
          type="button"
          onClick={() => goTo(1)}
          disabled={index >= items.length - 1 && !hasNextPage}
          aria-label={t("common.next")}
          className={cn(roundButtonClass, "hidden sm:flex")}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="relative flex flex-col items-center gap-3 px-3 sm:px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-5">
        {isFrameOnly && <PickerFrameStrip key={item._id} item={item} videoRef={videoRef} />}

        <div className="flex w-full items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goTo(-1)}
            disabled={index <= 0}
            aria-label={t("common.previous")}
            className={cn(roundButtonClass, "sm:hidden")}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {compatibility.status === "blocked" ? (
            <p className="flex min-w-0 items-center gap-2 rounded-xl bg-black/[0.05] dark:bg-white/[0.08] px-3.5 py-2.5 text-sm text-text-secondary dark:text-gray-300">
              <Ban className="w-4 h-4 shrink-0" />
              <span className="truncate">{t(compatibility.reasonKey)}</span>
            </p>
          ) : isFrameOnly ? (
            <p className="min-w-0 truncate text-sm text-text-tertiary dark:text-gray-400">
              {t("filePicker.frame.hint")}
            </p>
          ) : (
            <button
              type="button"
              onClick={handleToggle}
              disabled={isLimitReached}
              title={isLimitReached ? t("filePicker.tray.full") : undefined}
              className={
                isSelected
                  ? cn(
                      libraryGlowButtonClass,
                      "h-11 min-w-[160px] px-6 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-semibold bg-black/[0.06] dark:bg-white/[0.1] text-text-primary dark:text-white ring-1 ring-inset ring-black/[0.08] dark:ring-white/[0.12] transition-transform active:scale-[0.97]"
                    )
                  : cn(pickerPrimaryButtonClass, "h-11 min-w-[160px] px-6 gap-2 text-sm")
              }
            >
              {isSelected ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
              {isSelected ? t("filePicker.quickLook.deselect") : t("filePicker.quickLook.select")}
            </button>
          )}

          <button
            type="button"
            onClick={() => goTo(1)}
            disabled={index >= items.length - 1 && !hasNextPage}
            aria-label={t("common.next")}
            className={cn(roundButtonClass, "sm:hidden")}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
